import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Tabs, Tab, Table, TableHead, TableBody,
  TableRow, TableCell, TableContainer, TextField, Button, CircularProgress,
  Alert, Select, MenuItem, FormControl, InputLabel, Chip, FormControlLabel,
  Checkbox, Tooltip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4001';

// ── helpers ────────────────────────────────────────────────────────────────────
const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];
function buildYears() {
  const y = new Date().getFullYear();
  return [y - 2, y - 1, y, y + 1];
}
const num = (v: string | number | null | undefined): number | null =>
  v == null || v === '' || isNaN(Number(v)) ? null : Number(v);
const fmt = (v: number | null | undefined, dec = 0) =>
  v == null ? '—' : Number(v).toLocaleString('en-US', {
    minimumFractionDigits: dec, maximumFractionDigits: dec,
  });
const DAY_NAMES = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
const fmtDate = (iso: string) => {
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'long', day: '2-digit', year: 'numeric' });
};
const dayName = (iso: string) => DAY_NAMES[new Date(iso + 'T00:00:00').getDay()];

// ── Column structure (3-level headers) ───────────────────────────────────────
interface LeafCol { key: string; label: string; computed?: boolean; isTime?: boolean }
interface ColGroup { label: string; shortLabel?: string; bg: string; cols: LeafCol[] }
interface Section { label: string; bg: string; groups: ColGroup[] }

const SECTIONS: Section[] = [
  {
    label: 'Gas Meters (Nm³)', bg: '#e8f5e9',
    groups: [
      { label: 'Thermax Gas Totalizer 1', shortLabel: 'Thermax 1 Totalizer', bg: '#c8e6c9', cols: [
        { key: 'thermax1Totalizer', label: 'Totalizer' },
        { key: 'thermax1Output',    label: 'Output', computed: true },
      ]},
      { label: 'Thermax Gas Totalizer 2', shortLabel: 'Thermax 2 Totalizer', bg: '#b2dfdb', cols: [
        { key: 'thermax2Totalizer', label: 'Totalizer' },
        { key: 'thermax2Output',    label: 'Output', computed: true },
      ]},
    ],
  },
  {
    label: 'Gas Flare', bg: '#fff9c4',
    groups: [
      { label: 'Time', bg: '#fffde7', cols: [
        { key: 'gasFlareStartTime', label: 'Start', isTime: true },
        { key: 'gasFlareEndTime',   label: 'End',   isTime: true },
      ]},
      { label: 'Totalizer', bg: '#fff8e1', cols: [
        { key: 'gasFlareStartTotalizer', label: 'Start' },
        { key: 'gasFlareEndTotalizer',   label: 'End' },
      ]},
      { label: 'Total Volume', bg: '#f9a825', cols: [
        { key: 'gasFlareTotalVolume', label: 'Nm³', computed: true },
      ]},
    ],
  },
  {
    label: 'Gas Engine 1 Power Meter (kWh)', bg: '#e3f2fd',
    groups: [{ label: '', bg: '#bbdefb', cols: [
      { key: 'engine1Totalizer', label: 'Totalizer' },
      { key: 'engine1Output',    label: 'Output', computed: true },
    ]}],
  },
  {
    label: 'Gas Engine 2 Power Meter (kWh)', bg: '#fce4ec',
    groups: [{ label: '', bg: '#f8bbd0', cols: [
      { key: 'engine2Totalizer', label: 'Totalizer' },
      { key: 'engine2Output',    label: 'Output', computed: true },
    ]}],
  },
  {
    label: 'Diesel Consumption 1', bg: '#fff3e0',
    groups: [{ label: '', bg: '#ffe0b2', cols: [
      { key: 'diesel1Totalizer', label: 'Totalizer' },
      { key: 'diesel1Output',    label: 'Output', computed: true },
    ]}],
  },
  {
    label: 'Diesel Consumption 2', bg: '#efebe9',
    groups: [{ label: '', bg: '#d7ccc8', cols: [
      { key: 'diesel2Totalizer', label: 'Totalizer' },
      { key: 'diesel2Output',    label: 'Output', computed: true },
    ]}],
  },
  {
    label: 'Diesel Usage', bg: '#f3e5f5',
    groups: [{ label: '', bg: '#e1bee7', cols: [
      { key: 'dieselUsage', label: 'Output', computed: true },
    ]}],
  },
  {
    label: 'Gas Storage', bg: '#e8eaf6',
    groups: [
      { label: 'Fermenter %', bg: '#c5cae9', cols: [
        { key: 'f1GasStoragePct', label: 'F1 (%)' },
        { key: 'f2GasStoragePct', label: 'F2 (%)' },
        { key: 'f3GasStoragePct', label: 'F3 (%)' },
      ]},
      { label: 'Totalizer', bg: '#9fa8da', cols: [
        { key: 'gasStorageTotalizer', label: 'Totalizer' },
        { key: 'gasStorageOutput',    label: 'Output', computed: true },
      ]},
    ],
  },
];

const ALL_LEAF_COLS = SECTIONS.flatMap(s => s.groups.flatMap(g => g.cols));
const INPUT_KEYS   = ALL_LEAF_COLS.filter(c => !c.computed).map(c => c.key);

// ── cell styles ───────────────────────────────────────────────────────────────
const TH = (bg = '#1b5e20', color = '#fff') => ({
  background: bg, color,
  fontWeight: 700, fontSize: '0.58rem', textAlign: 'center' as const,
  px: '2px', py: '4px', borderRight: '1px solid rgba(0,0,0,0.12)',
  whiteSpace: 'nowrap' as const, lineHeight: 1.1,
});
const TD: React.CSSProperties = {
  padding: '2px 4px', fontSize: '0.68rem', textAlign: 'right',
  whiteSpace: 'nowrap', borderRight: '1px solid rgba(0,0,0,0.06)',
};
const TD_COMP: React.CSSProperties = { ...TD, fontWeight: 700, background: '#f9fbe7', color: '#33691e' };
const TD_CUT: React.CSSProperties  = { ...TD, background: '#fff9c4' };

// ── main component ────────────────────────────────────────────────────────────
const DailyBiogasMonitoring: React.FC = () => {
  const navigate = useNavigate();
  const today = new Date().toISOString().split('T')[0];
  const [tab, setTab] = useState(0);

  // ── Report state ──────────────────────────────────────────────────────────
  const [repMonth, setRepMonth]   = useState(new Date().getMonth() + 1);
  const [repYear,  setRepYear]    = useState(new Date().getFullYear());
  const [repData,  setRepData]    = useState<{ cutoff: any; rows: any[]; totals: any } | null>(null);
  const [repLoading, setRepLoading] = useState(false);
  const [repError,   setRepError]   = useState('');

  // ── Entry state ───────────────────────────────────────────────────────────
  const [entDate,    setEntDate]    = useState(today);
  const [isCutoff,   setIsCutoff]   = useState(false);
  const [edits,      setEdits]      = useState<Record<string, string>>({});
  const [entLoading, setEntLoading] = useState(false);
  const [entSaving,  setEntSaving]  = useState(false);
  const [entError,   setEntError]   = useState('');
  const [entSuccess, setEntSuccess] = useState('');

  // ── Report fetch ──────────────────────────────────────────────────────────
  const loadReport = useCallback(async (month: number, year: number) => {
    setRepLoading(true); setRepError('');
    try {
      const { data } = await axios.get(`${API}/daily-biogas/monthly-report`, {
        params: { month: `${year}-${String(month).padStart(2, '0')}` },
      });
      setRepData(data);
    } catch { setRepError('Failed to load report.'); }
    finally { setRepLoading(false); }
  }, []);

  useEffect(() => { if (tab === 0) loadReport(repMonth, repYear); }, [tab, repMonth, repYear, loadReport]);

  // ── Entry fetch ───────────────────────────────────────────────────────────
  const loadEntry = useCallback(async (date: string) => {
    setEntLoading(true); setEntError(''); setEntSuccess('');
    setEdits({}); setIsCutoff(false);
    try {
      const { data } = await axios.get(`${API}/daily-biogas/date-report`, { params: { date } });
      if (data) {
        setIsCutoff(data.isCutoff ?? false);
        const map: Record<string, string> = {};
        for (const k of [...INPUT_KEYS, 'remarks']) {
          if (data[k] != null) map[k] = String(data[k]);
        }
        setEdits(map);
      }
    } catch { setEntError('Failed to load existing data.'); }
    finally { setEntLoading(false); }
  }, []);

  useEffect(() => { if (tab === 1) loadEntry(entDate); }, [tab, entDate, loadEntry]);

  const set = (k: string, v: string) => setEdits(prev => ({ ...prev, [k]: v }));

  // ── Gas flare total volume (live computed preview) ─────────────────────────
  const liveGasFlareTotalVol = () => {
    const start = num(edits['gasFlareStartTotalizer']);
    const end   = num(edits['gasFlareEndTotalizer']);
    return start != null && end != null ? end - start : null;
  };

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    setEntSaving(true); setEntError(''); setEntSuccess('');
    try {
      const entry: any = { readingDate: entDate, isCutoff };
      for (const k of INPUT_KEYS) {
        const n = num(edits[k]);
        if (n !== null) entry[k] = n;
        else if (k.includes('Time') && edits[k]) entry[k] = edits[k]; // time strings
      }
      if (edits['remarks']) entry.remarks = edits['remarks'];
      await axios.post(`${API}/daily-biogas/batch`, { entries: [entry] });
      setEntSuccess(`Saved ${isCutoff ? 'cutoff (baseline)' : 'daily'} reading for ${fmtDate(entDate)}.`);
      if (tab === 0) loadReport(repMonth, repYear);
    } catch { setEntError('Save failed. Please try again.'); }
    finally { setEntSaving(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete data for ${fmtDate(entDate)}?`)) return;
    try {
      await axios.delete(`${API}/daily-biogas/by-date`, { params: { date: entDate } });
      setEdits({}); setIsCutoff(false);
      setEntSuccess('Deleted.');
    } catch { setEntError('Delete failed.'); }
  };

  // ── Render report cell ─────────────────────────────────────────────────────
  const repCell = (row: any, col: LeafCol, isCut = false): React.ReactNode => {
    const v = row?.[col.key];
    const style = col.computed ? TD_COMP : (isCut ? TD_CUT : TD);
    const text = col.isTime
      ? (v ?? '—')
      : fmt(v, col.key.includes('Flare') && col.key.includes('Time') ? 0 : 0);
    return (
      <TableCell key={col.key} style={style}>{text}</TableCell>
    );
  };

  // Total leaf columns count
  const totalLeafCols = ALL_LEAF_COLS.length;

  return (
    <Box sx={{ p: 3, maxWidth: '100%' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} size="small" variant="outlined">Back</Button>
        <MonitorHeartIcon sx={{ color: '#1b5e20', fontSize: 32 }} />
        <Box>
          <Typography variant="h5" fontWeight={700} color="#1b5e20">Daily Biogas Usage Monitoring</Typography>
          <Typography variant="body2" color="text.secondary">
            Note: Recording should be done 1st hour of the day
          </Typography>
        </Box>
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Monthly Report" />
        <Tab label="Data Entry" />
      </Tabs>

      {/* ── MONTHLY REPORT ─── */}
      {tab === 0 && (
        <Box>
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>Month</InputLabel>
              <Select value={repMonth} label="Month" onChange={e => setRepMonth(Number(e.target.value))}>
                {MONTHS.map((m, i) => <MenuItem key={i} value={i + 1}>{m}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 90 }}>
              <InputLabel>Year</InputLabel>
              <Select value={repYear} label="Year" onChange={e => setRepYear(Number(e.target.value))}>
                {buildYears().map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
              </Select>
            </FormControl>
            <Chip label={`${repData?.rows?.length ?? 0} day(s)`} size="small" color="success" variant="outlined" />
          </Box>

          {repError && <Alert severity="error" sx={{ mb: 2 }}>{repError}</Alert>}
          {repLoading ? <CircularProgress size={28} /> : (
            <TableContainer component={Paper} sx={{ maxHeight: '72vh', overflow: 'auto' }}>
              <Table stickyHeader size="small" sx={{ minWidth: 3200 }}>
                <TableHead>
                  {/* ── Row 1: Section headers ── */}
                  <TableRow>
                    <TableCell rowSpan={3} sx={TH()}>Date</TableCell>
                    <TableCell rowSpan={3} sx={TH()}>Day</TableCell>
                    {SECTIONS.map(s => {
                      const leafCount = s.groups.reduce((a, g) => a + g.cols.length, 0);
                      const hasGroups = s.groups.some(g => g.label !== '');
                      return (
                        <TableCell key={s.label} colSpan={leafCount}
                          rowSpan={hasGroups ? 1 : 2}
                          sx={{ ...TH(s.bg, '#000'), borderLeft: '2px solid rgba(0,0,0,0.2)', fontSize: '0.6rem' }}>
                          {s.label}
                        </TableCell>
                      );
                    })}
                    <TableCell rowSpan={3} sx={TH()}>Remarks</TableCell>
                  </TableRow>
                  {/* ── Row 2: Group headers ── */}
                  <TableRow>
                    {SECTIONS.flatMap(s =>
                      s.groups.filter(g => g.label !== '').map(g => (
                        <TableCell key={`${s.label}-${g.label}`} colSpan={g.cols.length}
                          sx={{ ...TH(g.bg, '#000'), borderLeft: '1px solid rgba(0,0,0,0.15)' }}>
                          {g.label}
                        </TableCell>
                      ))
                    )}
                  </TableRow>
                  {/* ── Row 3: Leaf column headers ── */}
                  <TableRow>
                    {SECTIONS.flatMap(s => s.groups.flatMap(g => g.cols.map(c => (
                      <TableCell key={c.key}
                        sx={TH(c.computed ? '#aed581' : g.bg, '#000')}>
                        {c.label}
                      </TableCell>
                    ))))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {/* ── Cutoff row (yellow baseline) ── */}
                  {repData?.cutoff && (
                    <TableRow sx={{ background: '#fff59d' }}>
                      <TableCell sx={{ ...TD, background: '#fff59d', fontWeight: 700, color: '#e65100', fontSize: '0.65rem' }}>
                        {fmtDate(repData.cutoff.readingDate)}
                      </TableCell>
                      <TableCell sx={{ ...TD, background: '#fff59d', fontWeight: 700 }}>
                        {dayName(repData.cutoff.readingDate)}
                      </TableCell>
                      {ALL_LEAF_COLS.map(c => (
                        <TableCell key={c.key} style={{ ...TD_CUT, fontWeight: 700 }}>
                          {c.isTime ? (repData.cutoff[c.key] ?? '—') : fmt(repData.cutoff[c.key])}
                        </TableCell>
                      ))}
                      <TableCell style={TD_CUT}>{repData.cutoff.remarks ?? ''}</TableCell>
                    </TableRow>
                  )}

                  {/* ── Data rows ── */}
                  {repData?.rows?.map((row: any) => (
                    <TableRow key={row.readingDate} hover>
                      <TableCell sx={{ ...TD, fontWeight: 600, textAlign: 'left', whiteSpace: 'nowrap' }}>
                        {fmtDate(row.readingDate)}
                      </TableCell>
                      <TableCell sx={{ ...TD, textAlign: 'center' }}>{dayName(row.readingDate)}</TableCell>
                      {ALL_LEAF_COLS.map(c =>
                        repCell(row, c)
                      )}
                      <TableCell style={TD}>{row.remarks ?? ''}</TableCell>
                    </TableRow>
                  ))}

                  {(!repData?.rows?.length) && (
                    <TableRow>
                      <TableCell colSpan={totalLeafCols + 3} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        No data for {MONTHS[repMonth - 1]} {repYear}
                      </TableCell>
                    </TableRow>
                  )}

                  {/* ── Totals row ── */}
                  {repData && (repData.rows?.length ?? 0) > 0 && (() => {
                    const t = repData.totals;
                    return (
                      <TableRow sx={{ borderTop: '3px solid #1b5e20', background: '#e8f5e9' }}>
                        <TableCell colSpan={2} sx={{ ...TD, fontWeight: 800, background: '#1b5e20', color: '#fff', textAlign: 'center', fontSize: '0.7rem' }}>
                          Total / Average
                        </TableCell>
                        {ALL_LEAF_COLS.map(c => (
                          <TableCell key={c.key}
                            style={{ ...TD, fontWeight: 700, background: c.computed ? '#dcedc8' : '#e8f5e9' }}>
                            {c.isTime ? '—' : fmt(t?.[c.key])}
                          </TableCell>
                        ))}
                        <TableCell style={{ ...TD, background: '#e8f5e9' }} />
                      </TableRow>
                    );
                  })()}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {/* ── DATA ENTRY TAB ─── */}
      {tab === 1 && (
        <Box>
          <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              label="Date" type="date" size="small"
              value={entDate} onChange={e => setEntDate(e.target.value)}
              InputLabelProps={{ shrink: true }} sx={{ width: 160 }}
            />
            <Tooltip title="Mark this row as the previous-month cut-off/baseline (shown in yellow on the report)">
              <FormControlLabel
                control={<Checkbox checked={isCutoff} onChange={e => setIsCutoff(e.target.checked)}
                  sx={{ color: '#f9a825', '&.Mui-checked': { color: '#f9a825' } }} />}
                label={<Typography variant="body2">Cut-off / Baseline row</Typography>}
              />
            </Tooltip>
            <Button variant="contained" startIcon={entSaving ? <CircularProgress size={16} /> : <SaveIcon />}
              onClick={handleSave} disabled={entSaving || entLoading}
              sx={{ bgcolor: '#1b5e20', '&:hover': { bgcolor: '#2e7d32' } }}>
              Save
            </Button>
            <Button variant="outlined" color="error" startIcon={<DeleteSweepIcon />}
              onClick={handleDelete} disabled={entSaving || entLoading}>
              Delete
            </Button>
          </Box>

          {entError   && <Alert severity="error"   sx={{ mb: 1 }}>{entError}</Alert>}
          {entSuccess && <Alert severity="success" sx={{ mb: 1 }}>{entSuccess}</Alert>}
          {entLoading && <CircularProgress size={24} sx={{ mb: 1 }} />}

          {/* Entry form organised by section */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {SECTIONS.map(section => {
              const inputCols = section.groups.flatMap(g => g.cols.filter(c => !c.computed));
              if (inputCols.length === 0) return null;
              return (
                <Paper key={section.label} variant="outlined" sx={{ p: 2, borderLeft: `4px solid ${section.bg}` }}>
                  <Typography variant="subtitle2" fontWeight={700} mb={1.5} color="#1b5e20">
                    {section.label}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                    {section.groups.map(group =>
                      group.cols.map(col => {
                        if (col.computed) return null;
                        return (
                          <TextField
                            key={col.key}
                            label={(group.shortLabel ?? group.label)
                              ? `${group.shortLabel ?? group.label}${group.cols.filter(c => !c.computed).length > 1 ? ` — ${col.label}` : ''}`
                              : col.label}
                            size="small"
                            type={col.isTime ? 'text' : 'number'}
                            value={edits[col.key] ?? ''}
                            onChange={e => set(col.key, e.target.value)}
                            placeholder={col.isTime ? 'e.g. 0716h' : undefined}
                            sx={{ width: col.isTime ? 140 : 210 }}
                          />
                        );
                      })
                    )}
                    {/* Live computed preview for Gas Flare */}
                    {section.label === 'Gas Flare' && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1,
                        px: 2, bgcolor: '#f9fbe7', borderRadius: 1, border: '1px solid #c5e1a5' }}>
                        <Typography variant="caption" color="text.secondary">Total Volume (Nm³):</Typography>
                        <Typography variant="body2" fontWeight={700} color="#33691e">
                          {liveGasFlareTotalVol() != null ? fmt(liveGasFlareTotalVol()) : '—'}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Paper>
              );
            })}

            {/* Remarks */}
            <Paper variant="outlined" sx={{ p: 2 }}>
              <TextField
                label="Remarks" multiline rows={2} fullWidth size="small"
                value={edits['remarks'] ?? ''}
                onChange={e => set('remarks', e.target.value)}
              />
            </Paper>
          </Box>

          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Output values are computed automatically from totalizer readings.
            Day 1 output = Day 1 totalizer − previous month cut-off totalizer.
            Subsequent days = today − previous day totalizer.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default DailyBiogasMonitoring;
